import { isRefusalEnvelope, RobotoDomainException } from "@/shared/http";

import { type SessionFile } from "./SessionFile";
import {
  type CreateSessionRequest,
  type SessionFileDeclaration,
  type SessionRecord,
} from "./SessionRecord";
import {
  MAX_FILES_PER_ADD_FILES_REQUEST,
  SessionService,
} from "./SessionService";

export interface SessionCompositionContext {
  readonly operationId: string;
  readonly orgId: string;
}

interface SelectedFile {
  readonly originalIndex: number;
  readonly declaration: Readonly<SessionFileDeclaration>;
}

/** What a check of the session's file list saw of a file whose add was never confirmed. */
type SessionCompositionObservation = "absent" | "unreadable" | "conflict";

export type SessionCompositionEntry = SelectedFile &
  (
    | { readonly status: "included" }
    | { readonly status: "refused"; readonly error: RobotoDomainException }
    | { readonly status: "notAttempted" }
    | {
        readonly status: "unknown";
        /** True once a read of the session's file list has been attempted for this entry,
         * whether or not that read succeeded. Retrying an entry by index requires it. */
        readonly reconciliationAttempted?: boolean;
        readonly observation?: SessionCompositionObservation;
      }
  );

/** Why a composition sent no further batch: the caller asked it to stop, an add-files request failed
 * (`request`), or its answer could not be read (`protocol`). */
export type SessionCompositionStopCause =
  | { readonly kind: "stopped" }
  | { readonly kind: "request" | "protocol"; readonly error: unknown };

export interface SessionCompositionOutcome {
  readonly context: Readonly<SessionCompositionContext>;
  readonly sessionId?: string;
  readonly entries: readonly SessionCompositionEntry[];
  /** True once any add-files request might have reached the server. */
  readonly hasDispatched: boolean;
  readonly stopCause?: SessionCompositionStopCause;
  readonly reconciliationError?: unknown;
}

export interface SessionCompositionOptions {
  /** Stops requests that have not been sent yet. A request already in flight still runs, as do its
   * transport-level retries. */
  stopRequested?: () => boolean;
  onProgress?: (outcome: SessionCompositionOutcome) => void;
}

export type SessionCompositionRetrySelection =
  | { kind: "remaining" }
  | {
      kind: "unknown" | "replaceConflicts";
      originalIndices: readonly number[];
    };

export type SessionCreationResult =
  | {
      readonly kind: "completed";
      readonly sessionId: string;
      readonly outcome: SessionCompositionOutcome;
    }
  | {
      readonly kind: "creationUnknown";
      readonly error: unknown;
      readonly outcome: SessionCompositionOutcome;
    }
  | {
      readonly kind: "compensated";
      readonly sessionId: string;
      readonly outcome: SessionCompositionOutcome;
    }
  | {
      readonly kind: "cleanupUnknown";
      readonly sessionId: string;
      readonly outcome: SessionCompositionOutcome;
      readonly cleanupError: unknown;
      readonly survivingSession?: SessionRecord;
      readonly checkError?: unknown;
    };

/** The server answered in a shape this client cannot read, so what the request did is unknown. Every
 * throw site reports under this one name, so each states what it could not read. */
class MalformedSessionResponseError extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = "MalformedSessionResponseError";
  }
}

/** Every field the add-files body accepts. Typed against `SessionFileDeclaration` so a field added
 * there fails the build here until this list names it, rather than being silently refused at
 * runtime by `validateDeclaration`. */
const DECLARATION_FIELDS: Record<keyof SessionFileDeclaration, true> = {
  file_id: true,
  min_file_timestamp_ns: true,
  max_file_timestamp_ns: true,
};

/** Check and copy a selection of files before any of it is sent, so a caller's later edits to its own
 * selection do not change what is sent. Throws on a file the add-files request cannot carry; see
 * {@link validateDeclaration}. The same file selected twice collapses to a single entry; selected
 * twice with different start and end times, it throws. */
export function prepareSessionComposition(
  context: SessionCompositionContext,
  files: readonly SessionFileDeclaration[],
  sessionId?: string,
): SessionCompositionOutcome {
  const entries: SessionCompositionEntry[] = [];
  const declarationsByFileId = new Map<
    string,
    Readonly<SessionFileDeclaration>
  >();
  files.forEach((file, originalIndex) => {
    validateDeclaration(file);
    const previous = declarationsByFileId.get(file.file_id);
    if (previous !== undefined) {
      if (!sameDeclaration(previous, file)) {
        throw new Error(
          `File ${file.file_id} was selected twice with different start and end times.`,
        );
      }
      return;
    }
    const declaration = Object.freeze({ ...file });
    declarationsByFileId.set(file.file_id, declaration);
    entries.push({ originalIndex, declaration, status: "notAttempted" });
  });
  return freezeOutcome({
    context: Object.freeze({ ...context }),
    sessionId,
    entries,
    hasDispatched: false,
  });
}

export function getSessionCompositionCounts(
  outcome: SessionCompositionOutcome,
) {
  const counts = { included: 0, refused: 0, unknown: 0, notAttempted: 0 };
  for (const entry of outcome.entries) {
    counts[entry.status] += 1;
  }
  return counts;
}

/** Add every file the session refused and every file never sent. A file whose outcome is unknown is
 * left alone; resending it needs {@link retrySessionComposition} with its index. */
export function composeSessionFiles(
  service: SessionService,
  outcome: SessionCompositionOutcome,
  options?: SessionCompositionOptions,
): Promise<SessionCompositionOutcome> {
  return retrySessionComposition(
    service,
    outcome,
    { kind: "remaining" },
    options,
  );
}

/**
 * Send the files `selection` names, in batches, and report what the session did with each.
 *
 * Throws before sending anything when the outcome names no session, or when an index selection
 * names a file whose outcome is already settled; see {@link selectEntriesByIndex}.
 */
export function retrySessionComposition(
  service: SessionService,
  outcome: SessionCompositionOutcome,
  selection: SessionCompositionRetrySelection,
  options?: SessionCompositionOptions,
): Promise<SessionCompositionOutcome> {
  const sessionId = outcome.sessionId;
  if (!sessionId) {
    throw new Error("A session is required before adding files.");
  }
  let selected: readonly SessionCompositionEntry[];
  switch (selection.kind) {
    case "remaining":
      selected = outcome.entries.filter(
        (entry) =>
          entry.status === "refused" || entry.status === "notAttempted",
      );
      break;
    case "unknown":
    case "replaceConflicts":
      selected = selectEntriesByIndex(outcome, selection);
      break;
    default: {
      // A selection kind added without a branch here fails the build at this assignment; the throw
      // refuses a kind that reaches here at run time anyway.
      const unhandled: never = selection;
      throw new Error(
        `Unhandled retry selection ${JSON.stringify(unhandled)}.`,
      );
    }
  }
  return executeBatches(service, sessionId, outcome, selected, options);
}

/** Resolve every unknown entry against the session's own file list, read through to its last page. A
 * read that fails leaves every unknown entry unknown and records why.
 *
 * A file the listing does not carry is reported absent. The listing omits a file whose upload is
 * incomplete and one that is being deleted, so absence is evidence that the add did not land rather
 * than proof of it. */
export async function reconcileSessionComposition(
  service: SessionService,
  outcome: SessionCompositionOutcome,
  options?: { abortSignal?: AbortSignal },
): Promise<SessionCompositionOutcome> {
  if (!outcome.sessionId) {
    throw new Error("A session is required before checking its files.");
  }
  if (!outcome.entries.some((entry) => entry.status === "unknown")) {
    return outcome;
  }
  let rows: SessionFile[];
  try {
    rows = await service.listAllSessionFiles(outcome.sessionId, {
      consistency: "strongly_consistent",
      abortSignal: options?.abortSignal,
    });
  } catch (error) {
    return freezeOutcome({
      ...outcome,
      reconciliationError: error,
      entries: outcome.entries.map((entry): SessionCompositionEntry =>
        entry.status === "unknown"
          ? { ...entry, reconciliationAttempted: true }
          : entry,
      ),
    });
  }
  const rowsByFileId = new Map<string, SessionFile[]>();
  for (const row of rows) {
    if (isObject(row) && typeof row.file_id === "string") {
      const sameFile = rowsByFileId.get(row.file_id);
      if (sameFile) {
        sameFile.push(row);
      } else {
        rowsByFileId.set(row.file_id, [row]);
      }
    }
  }
  return freezeOutcome({
    ...outcome,
    reconciliationError: undefined,
    entries: outcome.entries.map((entry): SessionCompositionEntry => {
      if (entry.status !== "unknown") {
        return entry;
      }
      const matches = rowsByFileId.get(entry.declaration.file_id) ?? [];
      // Two rows for one file leave no single window to compare against, so the file's window is
      // unreadable rather than absent.
      if (matches.length !== 1) {
        return {
          ...entry,
          reconciliationAttempted: true,
          observation: observationOrEarlierConflict(
            entry.observation,
            matches.length === 0 ? "absent" : "unreadable",
          ),
        };
      }
      const observation = compareDeclaration(entry.declaration, matches[0]);
      if (observation === "included") {
        return {
          originalIndex: entry.originalIndex,
          declaration: entry.declaration,
          status: "included",
        };
      }
      return {
        ...entry,
        reconciliationAttempted: true,
        observation: observationOrEarlierConflict(
          entry.observation,
          observation,
        ),
      };
    }),
  });
}

/**
 * Create one session and add this selection's files to it.
 *
 * 1. Refuses a selection that already names a session or has already sent a file, so no call here
 *    creates a second session for the same selection.
 * 2. Creates the session, unless a stop was already requested, which rejects without creating one. A
 *    create whose answer cannot be read is reported `creationUnknown`: the session may exist, and
 *    nothing is deleted on a session this call cannot name.
 * 3. Adds the files. When every file is included, the result is `completed`.
 * 4. Otherwise deletes the session it created, so a session holding only some of the files is not
 *    left behind: `compensated` when the delete succeeds, `cleanupUnknown` when it fails, which
 *    {@link checkSessionCreation} can resolve.
 * 5. When `stopRequested` or `onProgress` throws after the create, deletes the session it created
 *    and rethrows that error.
 */
export async function createSessionWithFiles(
  service: SessionService,
  request: CreateSessionRequest,
  outcome: SessionCompositionOutcome,
  options?: SessionCompositionOptions,
): Promise<SessionCreationResult> {
  if (
    outcome.sessionId !== undefined ||
    outcome.hasDispatched ||
    outcome.entries.some((entry) => entry.status !== "notAttempted")
  ) {
    throw new Error(
      "Creating a session needs a selection that names no session and has sent no file; build a new one.",
    );
  }
  // Copy before awaiting the create so later form edits cannot change what this call sends.
  const originalRequest = structuredClone(request);
  if (options?.stopRequested?.()) {
    throw new Error("Session creation was stopped before submission.");
  }
  let sessionId: string;
  try {
    const session = await service.createSession(originalRequest, {
      orgId: outcome.context.orgId,
    });
    if (
      !isObject(session) ||
      typeof session.session_id !== "string" ||
      !session.session_id ||
      session.org_id !== outcome.context.orgId
    ) {
      throw new MalformedSessionResponseError(
        "The create response did not name a session in the requested org.",
      );
    }
    sessionId = session.session_id;
  } catch (error) {
    return { kind: "creationUnknown", error, outcome };
  }
  const created = freezeOutcome({ ...outcome, sessionId });
  let composed: SessionCompositionOutcome;
  try {
    composed = await composeSessionFiles(service, created, options);
  } catch (error) {
    // A caller's callback threw partway, so the session may hold any number of the files. The
    // callback's error is the one passed on; a delete that fails leaves the session to show up in
    // the session list.
    await service.deleteSession(sessionId).catch(() => undefined);
    throw error;
  }
  if (composed.entries.every((entry) => entry.status === "included")) {
    return { kind: "completed", sessionId, outcome: composed };
  }
  try {
    await service.deleteSession(sessionId);
    return { kind: "compensated", sessionId, outcome: composed };
  } catch (cleanupError) {
    return {
      kind: "cleanupUnknown",
      sessionId,
      outcome: composed,
      cleanupError,
    };
  }
}

/** Read back a session whose rollback delete failed. A read that returns the session shows it still
 * exists and reports it on `survivingSession`; a read that fails reports on `checkError` and leaves it
 * unknown whether the delete went through. */
export async function checkSessionCreation(
  service: SessionService,
  result: Extract<SessionCreationResult, { kind: "cleanupUnknown" }>,
  options?: { abortSignal?: AbortSignal },
): Promise<Extract<SessionCreationResult, { kind: "cleanupUnknown" }>> {
  try {
    const session = await service.getSessionById(result.sessionId, options);
    if (
      !isObject(session) ||
      session.session_id !== result.sessionId ||
      session.org_id !== result.outcome.context.orgId
    ) {
      throw new MalformedSessionResponseError(
        "The session read did not return the session this call created.",
      );
    }
    return { ...result, survivingSession: session, checkError: undefined };
  } catch (checkError) {
    // Clear any session a previous check found: this one could not confirm it is still there.
    return { ...result, survivingSession: undefined, checkError };
  }
}

async function executeBatches(
  service: SessionService,
  sessionId: string,
  original: SessionCompositionOutcome,
  selected: readonly SessionCompositionEntry[],
  options?: SessionCompositionOptions,
): Promise<SessionCompositionOutcome> {
  if (selected.length === 0) {
    return original;
  }
  let outcome = freezeOutcome({
    ...original,
    stopCause: undefined,
    reconciliationError: undefined,
  });
  for (
    let start = 0;
    start < selected.length;
    start += MAX_FILES_PER_ADD_FILES_REQUEST
  ) {
    if (options?.stopRequested?.()) {
      outcome = freezeOutcome({ ...outcome, stopCause: { kind: "stopped" } });
      options?.onProgress?.(outcome);
      break;
    }
    const batch = selected.slice(
      start,
      start + MAX_FILES_PER_ADD_FILES_REQUEST,
    );
    // Mark the batch unknown before the request goes out: from here on the server may have applied
    // it whatever the client hears back.
    outcome = mergeEntries(
      { ...outcome, hasDispatched: true },
      batch.map((entry) => ({
        originalIndex: entry.originalIndex,
        declaration: entry.declaration,
        status: "unknown",
      })),
    );
    options?.onProgress?.(outcome);
    let settledEntries: SessionCompositionEntry[];
    try {
      const response = await service.addFilesToSession(
        sessionId,
        batch.map((entry) => ({ ...entry.declaration })),
      );
      settledEntries = readBatchResponse(response, batch);
    } catch (error) {
      // Leave this batch unknown and send no more: a transport failure and an unreadable answer
      // both leave the server's state unknown, so the next batch would compound the uncertainty.
      outcome = freezeOutcome({
        ...outcome,
        stopCause: {
          kind:
            error instanceof MalformedSessionResponseError
              ? "protocol"
              : "request",
          error,
        },
      });
      options?.onProgress?.(outcome);
      break;
    }
    outcome = mergeEntries(outcome, settledEntries);
    options?.onProgress?.(outcome);
  }
  return outcome;
}

function readBatchResponse(
  response: unknown,
  batch: readonly SessionCompositionEntry[],
): SessionCompositionEntry[] {
  // The route answers with one element per file sent, in request order, so a response of any other
  // shape leaves every file in the batch unaccounted for.
  if (
    !isObject(response) ||
    !Array.isArray(response.responses) ||
    response.responses.length !== batch.length
  ) {
    throw new MalformedSessionResponseError(
      "The add-files response did not carry one entry per file sent.",
    );
  }
  const elements: unknown[] = response.responses;
  return batch.map((entry, index) => {
    const element = elements[index];
    if (!isObject(element)) {
      throw new MalformedSessionResponseError(
        "An add-files response entry was not an object.",
      );
    }
    const hasData = element.data !== undefined && element.data !== null;
    const hasError = element.error !== undefined && element.error !== null;
    if (hasData === hasError) {
      throw new MalformedSessionResponseError(
        "An add-files response entry carried both a file and an error, or neither.",
      );
    }
    if (hasData) {
      if (
        !isObject(element.data) ||
        element.data.file_id !== entry.declaration.file_id
      ) {
        throw new MalformedSessionResponseError(
          "An add-files response entry did not carry the file it was sent for.",
        );
      }
      return {
        originalIndex: entry.originalIndex,
        declaration: entry.declaration,
        status: "included",
      };
    }
    if (!isRefusalEnvelope(element.error)) {
      throw new MalformedSessionResponseError(
        "An add-files refusal carried no error_code and message.",
      );
    }
    return {
      originalIndex: entry.originalIndex,
      declaration: entry.declaration,
      status: "refused",
      error: RobotoDomainException.fromHttpResponse(element.error.error),
    };
  });
}

/**
 * What one row of the session's file listing says about a declaration this client sent.
 *
 * 1. "included": the session holds exactly the part of the file the declaration asked for.
 * 2. "conflict": a row this client can read that covers a different part of the file: a position
 *    slice, a time window that converts back to other bounds, or a whole file where a window was
 *    declared and the reverse.
 * 3. "unreadable": a row it cannot compare at all: a field missing or of the wrong type, a window
 *    carrying one bound or an inverted pair, or a stored window with no offset to convert it back
 *    to the file's own timestamps.
 */
function compareDeclaration(
  declaration: Readonly<SessionFileDeclaration>,
  sessionFile: SessionFile,
): "included" | "conflict" | "unreadable" {
  const {
    data_range: range,
    min_wall_clock_timestamp_ns: minimum,
    max_wall_clock_timestamp_ns: maximum,
    unix_epoch_offset_ns: offset,
  } = sessionFile;
  // This client only ever sends whole files, so a row carrying a position slice covers less of the
  // file than was declared. Anything else in that field is a row it cannot read.
  if (range !== null) {
    return Array.isArray(range) &&
      range.length === 2 &&
      range.every((value) => Number.isInteger(value)) &&
      range[0] < range[1]
      ? "conflict"
      : "unreadable";
  }
  if (
    (minimum !== null && typeof minimum !== "bigint") ||
    (maximum !== null && typeof maximum !== "bigint")
  ) {
    return "unreadable";
  }
  if (
    (minimum === null) !== (maximum === null) ||
    (minimum !== null && maximum !== null && minimum > maximum)
  ) {
    return "unreadable";
  }
  const declaredMin = declaration.min_file_timestamp_ns ?? null;
  const declaredMax = declaration.max_file_timestamp_ns ?? null;
  if (declaredMin === null || declaredMax === null) {
    return minimum === null && maximum === null ? "included" : "conflict";
  }
  if (minimum === null || maximum === null) {
    return "conflict";
  }
  if (typeof offset !== "bigint") {
    return "unreadable";
  }
  return minimum - offset === declaredMin && maximum - offset === declaredMax
    ? "included"
    : "conflict";
}

function freezeOutcome(
  outcome: SessionCompositionOutcome,
): SessionCompositionOutcome {
  return Object.freeze({
    ...outcome,
    entries: Object.freeze(
      outcome.entries.map((entry) => Object.freeze(entry)),
    ),
  });
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function mergeEntries(
  outcome: SessionCompositionOutcome,
  entries: readonly SessionCompositionEntry[],
): SessionCompositionOutcome {
  const byIndex = new Map(entries.map((entry) => [entry.originalIndex, entry]));
  return freezeOutcome({
    ...outcome,
    entries: outcome.entries.map(
      (entry) => byIndex.get(entry.originalIndex) ?? entry,
    ),
  });
}

/** A file the session was seen to hold over a different window stays reported as a conflict: a later
 * read that cannot find it, or cannot read its window, does not withdraw what the earlier read saw. */
function observationOrEarlierConflict(
  earlier: SessionCompositionObservation | undefined,
  observed: SessionCompositionObservation,
): SessionCompositionObservation {
  return earlier === "conflict" ? "conflict" : observed;
}

/**
 * The entries `selection` names by original index, refusing a selection this outcome cannot answer.
 *
 * 1. Every index must name an entry of this outcome.
 * 2. Every entry must still be unconfirmed after a read of the session's file list; a file the
 *    session included or refused outright has nothing left to resend.
 * 3. `replaceConflicts` takes only the files the session was seen to hold over a different window,
 *    and `unknown` only the files it was not, so overwriting a window a caller has not been shown
 *    cannot be requested by passing the wrong kind.
 */
function selectEntriesByIndex(
  outcome: SessionCompositionOutcome,
  selection: Extract<
    SessionCompositionRetrySelection,
    { originalIndices: readonly number[] }
  >,
): readonly SessionCompositionEntry[] {
  const indices = new Set(selection.originalIndices);
  const selected = outcome.entries.filter((entry) =>
    indices.has(entry.originalIndex),
  );
  if (selected.length !== indices.size) {
    throw new Error(
      "Retry was asked for an original index this selection does not hold.",
    );
  }
  const wantsConflicts = selection.kind === "replaceConflicts";
  for (const entry of selected) {
    if (entry.status !== "unknown" || !entry.reconciliationAttempted) {
      throw new Error(
        "Only a file left unconfirmed by a check of the session's file list can be retried by index.",
      );
    }
    if (wantsConflicts !== (entry.observation === "conflict")) {
      throw new Error(
        wantsConflicts
          ? "Replacing start and end times is only for a file the session already holds over a different window."
          : "A file the session already holds over a different window is replaced, not retried.",
      );
    }
  }
  return selected;
}

function sameDeclaration(
  left: Readonly<SessionFileDeclaration>,
  right: Readonly<SessionFileDeclaration>,
): boolean {
  return (
    left.file_id === right.file_id &&
    (left.min_file_timestamp_ns ?? null) ===
      (right.min_file_timestamp_ns ?? null) &&
    (left.max_file_timestamp_ns ?? null) ===
      (right.max_file_timestamp_ns ?? null)
  );
}

/** Refuse a selected file the add-files request cannot carry, before any of the selection is sent.
 * A field outside {@link DECLARATION_FIELDS} is refused rather than dropped, so a caller that
 * expected it to reach the session hears about it instead of silently losing it. */
function validateDeclaration(file: SessionFileDeclaration): void {
  if (
    !isObject(file) ||
    typeof file.file_id !== "string" ||
    file.file_id.length === 0
  ) {
    throw new Error("Every selected file needs a non-empty file_id.");
  }
  const unknownFields = Object.keys(file).filter(
    (key) => !Object.hasOwn(DECLARATION_FIELDS, key),
  );
  if (unknownFields.length > 0) {
    throw new Error(
      `File ${file.file_id} carries fields this request cannot send: ${unknownFields.join(", ")}.`,
    );
  }
  const minimum = file.min_file_timestamp_ns ?? null;
  const maximum = file.max_file_timestamp_ns ?? null;
  if (
    (minimum === null) !== (maximum === null) ||
    (minimum !== null &&
      (typeof minimum !== "bigint" ||
        typeof maximum !== "bigint" ||
        minimum > maximum))
  ) {
    throw new Error(
      `File ${file.file_id} must set both min_file_timestamp_ns and max_file_timestamp_ns, with the minimum at or before the maximum.`,
    );
  }
}
